import { useState } from 'react';
import FamilyList from './FamilyList';
import ActionsMenu from '../../../../components/ActionsMenu';
import SelectorSearchSortBar from '../../../../components/SelectorSearchSortBar';
import { useToolAccess } from '../../../../tools/ToolAccessContext';

/**
 * FamiliesSelector component
 * Left pane container for selecting families
 */
export default function FamiliesSelector({
    loading,
    families,
    projects,
    filteredFamilies,
    selectedFamilyId,
    searchQuery,
    sortBy,
    sortOrder,
    isEditMode,
    fileInputRef,
    onSelectFamily,
    onEditFamily,
    onCreateFamily,
    onImportClick,
    onFileChange,
    onSearchChange,
    onSortByChange,
    onSortOrderToggle,
    onExportFamily,
    onShareFamily,
    onDeleteFamily,
    onLinkExistingFamily,
    onOpenSimulator,
    activeProjectId
}) {
    const { canEditCurrent } = useToolAccess();
    const [searchOpen, setSearchOpen] = useState(false);

    const handleToggleSearch = () => {
        if (searchOpen) {
            onSearchChange('');
            setSearchOpen(false);
            return;
        }
        setSearchOpen(true);
    };

    const menuItems = [
        {
            label: 'Search',
            icon: 'fa-solid fa-magnifying-glass',
            onSelect: handleToggleSearch
        },
        ...(activeProjectId ? [{
            label: 'Link',
            icon: 'fa-solid fa-link',
            disabled: !canEditCurrent,
            onSelect: onLinkExistingFamily
        }] : []),
        {
            label: 'Import Family',
            icon: 'fa-solid fa-file-import',
            onSelect: onImportClick
        }
    ];

    return (
        <div style={{
            width: '320px',
            minWidth: '320px',
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-card)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: '0.7rem 1rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                background: 'var(--bg-card)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Families</h2>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>Family Domains</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <button
                            type="button"
                            className="actions-menu-trigger"
                            title="Run Simulator"
                            aria-label="Run Simulator"
                            onClick={() => onOpenSimulator()}
                        >
                            <i className="fa-solid fa-flask-gear"></i>
                        </button>
                        <ActionsMenu
                            label="Family actions"
                            items={menuItems}
                        />
                        <button 
                            onClick={() => onCreateFamily(true)}
                            className="submit-btn" 
                            disabled={!canEditCurrent}
                            style={{ 
                                margin: 0, 
                                padding: '0.5rem 0.75rem', 
                                fontSize: '0.85rem', 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '0.25rem' 
                            }}
                        >
                            <i className="fa-solid fa-plus"></i> New
                        </button>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={onFileChange} 
                            accept=".product,.family" 
                            style={{ display: 'none' }} 
                        />
                    </div>
                </div>

                {searchOpen && (
                    <SelectorSearchSortBar
                        searchQuery={searchQuery}
                        onSearchChange={onSearchChange}
                        searchPlaceholder="Search families..."
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortByChange={onSortByChange}
                        onSortOrderToggle={onSortOrderToggle}
                        autoFocus
                        sortOptions={[
                            { value: 'name', label: 'Name' },
                            { value: 'lastChangeDate', label: 'Date Modified' },
                            { value: 'createdAt', label: 'Date Created' },
                            { value: 'lastChangeUser', label: 'Created By' }
                        ]}
                    />
                )}
            </div>

            {/* Families List */}
            <FamilyList
                loading={loading}
                families={families}
                projects={projects}
                filteredFamilies={filteredFamilies}
                selectedFamilyId={selectedFamilyId}
                isEditMode={isEditMode}
                onSelectFamily={onSelectFamily}
                onEditFamily={onEditFamily}
                onExportFamily={onExportFamily}
                onShareFamily={onShareFamily}
                onDeleteFamily={onDeleteFamily}
                onOpenSimulator={onOpenSimulator}
            />
        </div>
    );
}
